import { gql } from './gql'

/**
 * The two rule types the User API resolves through the client system's
 * `GET_CHECKOUT_RULE` add-on. There is no third: shipping methods and fees
 * have no rule type at all (scoping doc, out of scope).
 */
export const CHECKOUT_RULE_TYPES = {
  deliveryDates: 'DELIVERY_DATES',
  paymentMethods: 'PAYMENT_METHODS',
} as const

export type CheckoutRuleType =
  (typeof CHECKOUT_RULE_TYPES)[keyof typeof CHECKOUT_RULE_TYPES]

/**
 * The options a shopper may choose from, fetched live from the client's own
 * system (credit that changes intraday, route-driven delivery windows).
 *
 * Each entry in `data` is `{ data, amount }` — an ISO date for delivery
 * dates, `CASH`/`CREDIT` for payment methods. The response schema is locked
 * to exactly that: no labels, no disabled flags. Labels are built client-side.
 */
export const GET_CHECKOUT_RULE = gql`
  query GetCheckoutRule(
    $storefrontName: String!
    $sessionUid: ID!
    $ruleType: String!
  ) {
    getCheckoutRule(
      storefrontName: $storefrontName
      sessionUid: $sessionUid
      ruleType: $ruleType
    ) {
      ruleType
      data
    }
  }
`

/**
 * Persists the shopper's choice onto the session, which is what carries it
 * into the order.
 *
 * The server re-fetches the options and validates `value` against the *fresh*
 * list with strict string equality — so the offered `data` string must be
 * echoed back **verbatim**. Parsing `"2026-08-12"` into a `Date` and sending
 * `.toISOString()` back submits a different string for the same instant and
 * earns a 400. Treat the option as an opaque token.
 *
 * A 400 can also mean the list legitimately shifted between read and write
 * (a cutoff passed, midnight rolled over); the recovery is to re-fetch and
 * re-prompt, not to retry the same value.
 */
export const SET_CHECKOUT_RULE_SESSION = gql`
  mutation SetCheckoutRuleSession(
    $storefrontName: String!
    $sessionUid: String!
    $ruleType: String!
    $value: JSON!
  ) {
    setCheckoutRuleSession(
      storefrontName: $storefrontName
      sessionUid: $sessionUid
      ruleType: $ruleType
      value: $value
    ) {
      ruleType
      value
    }
  }
`
